import {
  Activity,
  AppWindow,
  ChevronsUpDown,
  LayoutDashboard,
  LogOut,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth';
import { BreadcrumbEntityProvider, HeaderBreadcrumb } from '@/components/breadcrumbs';
import { useIsMobile } from '@/hooks/use-mobile';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';

const navigation = [
  { to: '/', label: 'Visão geral', icon: LayoutDashboard, end: true },
  { to: '/users', label: 'Utilizadores', icon: Users },
  { to: '/applications', label: 'Aplicações', icon: AppWindow },
  // TODO: Repor /requests após implementar o fluxo de pedidos na área pessoal.
  { to: '/audit', label: 'Auditoria', icon: Activity },
  { to: '/settings', label: 'Definições', icon: Settings },
];

function initials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <BreadcrumbEntityProvider>
    <SidebarProvider>
      <Sidebar collapsible="icon">
        {/* Recolhida, a barra tem 3rem: com p-3 o botão de 2rem transbordava e o logótipo saía do centro. */}
        <SidebarHeader className="border-sidebar-border p-3 group-data-[collapsible=icon]:p-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <NavLink to="/">
                  <img src="/logo-jugais.png" alt="" className="aspect-square size-8 shrink-0 rounded-lg" />
                  <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-semibold">Quinta de Jugais</span>
                    <span className="truncate text-xs text-sidebar-foreground/65">Serviço de Autenticação</span>
                  </div>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupLabel>Administração</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navigation.map(({ to, label, icon: Icon, end }) => {
                  const active = end ? location.pathname === to : location.pathname.startsWith(to);
                  return (
                    <SidebarMenuItem key={to}>
                      <SidebarMenuButton asChild isActive={active} tooltip={label}>
                        <NavLink to={to} end={end}>
                          <Icon />
                          <span>{label}</span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="border-sidebar-border p-3 group-data-[collapsible=icon]:p-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton size="lg">
                    <Avatar className="size-8 rounded-lg">
                      <AvatarFallback className="rounded-lg bg-sidebar-accent text-sidebar-accent-foreground">
                        {initials(user?.profile.displayName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">{user?.profile.displayName}</span>
                      <span className="truncate text-xs text-sidebar-foreground/65">{user?.email}</span>
                    </div>
                    <ChevronsUpDown className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side={isMobile ? 'top' : 'right'} align="end" className="w-64">
                  <DropdownMenuLabel>
                    <div className="flex flex-col gap-1">
                      <p>{user?.profile.displayName}</p>
                      <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/security')}>
                    <ShieldCheck />
                    A minha segurança
                  </DropdownMenuItem>
                  <DropdownMenuItem variant="destructive" onClick={() => void signOut()}>
                    <LogOut />
                    Terminar sessão
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
          {/* Crédito do autor: escondido quando a sidebar está recolhida em ícones. */}
          <p className="px-2 pt-1 text-[11px] leading-tight text-sidebar-foreground/45 group-data-[collapsible=icon]:hidden">
            Desenvolvido por{' '}
            <a
              href="https://neumotik.com"
              target="_blank"
              rel="noreferrer"
              className="underline-offset-2 hover:underline"
            >
              Neumotik
            </a>
          </p>
        </SidebarFooter>
        {/* Sem SidebarRail: a sua área de hover (16px) invadia a sidebar recolhida (48px) e a linha
            de 2px que desenhava à altura toda parecia uma barra de scroll. O botão do header basta. */}
      </Sidebar>
      <SidebarInset>
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b bg-background/95 px-4 backdrop-blur sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <SidebarTrigger />
            <Separator orientation="vertical" className="h-4" />
            <HeaderBreadcrumb />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-6">
            <Outlet />
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
    </BreadcrumbEntityProvider>
  );
}
